import type { FC } from 'react';
import { useContext, useEffect, lazy, Suspense } from 'react';
import { Route, Routes, Navigate, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { RequestProvider } from 'loti-request';
import { hot } from 'react-hot-loader/root';
import queryString from 'query-string';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'app/i18n';

import RealtimeProvider from 'app/common/RealtimeProvider';
import HashiWsProvider from 'app/common/HashiWsProvider';
import UserContext, { LOCALSTORAGE_TOKEN_KEY } from 'app/common/UserContext';
import { getHashiWsUrl, getHeaders, getWsUrl } from 'app/utils/api';
import WithAccess from 'app/common/WithAccess';
import { LOGIN_PATH, RESET_PASSWORD_PATH, SIGNUP_PATH } from 'app/constants/routes';

import NotFound from './common/NotFound';
import MonitoringPage from './pages/MonitoringPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { GlobalProvider } from './common/GlobalContext';
import DeleteBlueprintModal from './pages/BlueprintsPage/Blueprints/DeleteBlueprintModal';

const AuthReadmePage = lazy(() => import('./components/AuthReadme'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const EventPageContainer = lazy(() => import('./pages/EventsPage'));
const MatchPage = lazy(() => import('./pages/MatchPage'));
const BlueprintsPage = lazy(() => import('./pages/BlueprintsPage'));
const EditMatchBlueprintPage = lazy(() => import('./pages/EditMatchBlueprintPage'));
const NewBlueprintPage = lazy(() => import('./pages/NewBlueprintPage'));
const EditBlueprintPage = lazy(() => import('./pages/EditBlueprintPage'));
const IncidentsPage = lazy(() => import('./pages/IncidentsPage'));
const BlacklistPage = lazy(() => import('./pages/BlacklistPage'));
const TeamManagementPage = lazy(() => import('./pages/TeamManagementPage'));
const OutrightsPage = lazy(() => import('./pages/OutrightsPage'));
const OutrightsSeriesPage = lazy(() => import('./pages/OutrightsSeriesPage'));
const LaddersPage = lazy(() => import('./pages/LaddersPage'));
const OddsLadderPage = lazy(() => import('./pages/OddsLadderPage'));
const PairingLadderPage = lazy(() => import('./pages/PairingLadderPage'));
const BetlogPage = lazy(() => import('./pages/BetlogPage'));
const BettorListPage = lazy(() => import('./pages/BettorListPage'));
const BettorProfilePage = lazy(() => import('./pages/BettorProfilePage'));
const TicketPage = lazy(() => import('./pages/TicketPage'));

const App: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { currentUser, userType, status, login, preferences } = useContext(UserContext);
  const authenticating = status === 'LOADING';
  const token = currentUser ? currentUser.token : '';
  const authenticated = !!currentUser;

  useEffect(() => {
    const query = queryString.parse(location.search);
    if (typeof query.token === 'string') {
      localStorage.setItem(LOCALSTORAGE_TOKEN_KEY, query.token);
      navigate({ search: '' }, { replace: true });
    }
  }, [location]);

  // login user on mount with his token
  useEffect(() => {
    const token = localStorage.getItem(LOCALSTORAGE_TOKEN_KEY);
    if (token) {
      login({ token });
    }
  }, []);

  // set body class name for dark mode
  useEffect(() => {
    document.body.className = preferences.theme;
  }, [preferences.theme]);

  const getRedirectionState = () => {
    // avoid to have `from === LOGIN_PATH` to avoid login page redirect to itself on login success
    const from = location.pathname === LOGIN_PATH ? '/' : location.pathname + location.search;
    return { from };
  };

  useEffect(() => {
    if (
      !authenticated &&
      !authenticating &&
      ![SIGNUP_PATH, RESET_PASSWORD_PATH].includes(location.pathname)
    ) {
      navigate(LOGIN_PATH, { state: getRedirectionState() });
    }
  }, [authenticated, authenticating]);

  return (
    <Suspense fallback={null}>
      <ToastContainer
        position="top-right"
        autoClose={6000}
        hideProgressBar
        newestOnTop
        closeOnClick={false}
        pauseOnHover
        theme="light"
        pauseOnFocusLoss={false}
        style={{ zIndex: 1000000 }}
      />
      <Routes>
        <Route path={LOGIN_PATH} element={<LoginPage />} />
        <Route path={SIGNUP_PATH} element={<SignupPage />} />
        <Route path={RESET_PASSWORD_PATH} element={<ResetPasswordPage />} />
        {authenticated && (
          <Route
            path="/*"
            element={
              <RealtimeProvider url={getWsUrl()} params={{ token }} userType={userType}>
                <HashiWsProvider
                  url={getHashiWsUrl()}
                  params={{ token }}
                  userType={userType}
                  displayCompetitors={preferences.competitors_display ?? false}
                >
                  <RequestProvider headers={getHeaders(token)}>
                    <GlobalProvider>
                      <Routes>
                        <Route
                          path="/"
                          element={
                            <WithAccess feature="EVENTS_PAGE">
                              <EventPageContainer />
                            </WithAccess>
                          }
                        />
                        <Route
                          path="/matches"
                          element={
                            <WithAccess feature="MATCH_PAGE">
                              <MatchPage />
                            </WithAccess>
                          }
                        >
                          <Route path=":matchId" />
                          <Route path=":matchId/:gameId" />
                          <Route path=":matchId/:gameId/:view" />
                        </Route>
                        <Route
                          path="/blueprints"
                          element={
                            <WithAccess feature="BLUEPRINTS">
                              <BlueprintsPage />
                            </WithAccess>
                          }
                        >
                          <Route path=":videogameSlug" />
                          <Route
                            path=":videogameSlug/:eventType/:id/delete"
                            element={<DeleteBlueprintModal />}
                          />
                        </Route>
                        <Route path="/blueprints/:videogameSlug">
                          <Route path=":eventType/new" element={<NewBlueprintPage />} />
                          <Route path=":eventType/:id" element={<EditBlueprintPage />} />
                          <Route
                            path="match-blueprint/:matchId"
                            element={<EditMatchBlueprintPage />}
                          />
                        </Route>
                        <Route
                          path="/odds-ladders"
                          element={
                            <WithAccess feature="LADDER">
                              <Outlet />
                            </WithAccess>
                          }
                        >
                          <Route path="new" element={<OddsLadderPage />} />
                          <Route path=":id" element={<OddsLadderPage />} />
                          <Route path="" element={<LaddersPage ladderType="odds" key="odds" />} />
                        </Route>
                        <Route
                          path="/pairing-ladders"
                          element={
                            <WithAccess feature="LADDER">
                              <Outlet />
                            </WithAccess>
                          }
                        >
                          <Route path="new" element={<PairingLadderPage />} />
                          <Route path=":id" element={<PairingLadderPage />} />
                          <Route
                            path=""
                            element={<LaddersPage ladderType="pairing" key="pairing" />}
                          />
                        </Route>
                        <Route
                          path="/outrights"
                          element={
                            <WithAccess feature="OUTRIGHTS">
                              <OutrightsPage />
                            </WithAccess>
                          }
                        />
                        <Route
                          path="/outrights/series/:seriesId"
                          element={
                            <WithAccess feature="OUTRIGHTS">
                              <OutrightsSeriesPage />
                            </WithAccess>
                          }
                        />
                        <Route
                          path="/incidents"
                          element={
                            <WithAccess feature="INCIDENTS">
                              <IncidentsPage />
                            </WithAccess>
                          }
                        />
                        <Route
                          path="/blacklist"
                          element={
                            <WithAccess feature="BLACKLIST">
                              <BlacklistPage />
                            </WithAccess>
                          }
                        />
                        <Route
                          path="/monitoring"
                          element={
                            <WithAccess feature="MONITORING">
                              <MonitoringPage />
                            </WithAccess>
                          }
                        />
                        <Route
                          path="/team-management"
                          element={
                            <WithAccess
                              feature="TEAM_MANAGEMENT"
                              condition={currentUser.role === 'admin'}
                            >
                              <TeamManagementPage />
                            </WithAccess>
                          }
                        />
                        <Route
                          path="risk"
                          element={
                            <WithAccess feature="RISK_MANAGEMENT">
                              <Outlet />
                            </WithAccess>
                          }
                        >
                          <Route path="tickets" element={<BetlogPage />} />
                          <Route path="tickets/:id" element={<TicketPage />} />
                          <Route path="bettors" element={<BettorListPage />} />
                          <Route path="bettors/:id" element={<BettorProfilePage />} />
                        </Route>
                        <Route path="/auth_readme" element={<AuthReadmePage />} />
                        <Route path="/emergency" element={<Navigate to="/emergency.html" />} />
                        <Route path="/*" element={<NotFound />} />
                      </Routes>
                    </GlobalProvider>
                  </RequestProvider>
                </HashiWsProvider>
              </RealtimeProvider>
            }
          />
        )}
      </Routes>
    </Suspense>
  );
};

App.displayName = 'App';

export default hot(App);
